const normalizeStringArray = (value = []) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
};

const createGoalId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `goal-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

export const createEmptyCareGoal = () => ({
  id: createGoalId(),
  title: "",
  description: "",
  successCriteria: "",
  category: "",
  source: "",
  supportStrategy: "",
  status: "active",
  startDate: "",
  targetDate: "",
});

const normalizeCareGoal = (goal = {}) => ({
  id: String(goal.id || createGoalId()).trim(),
  title: String(goal.title || "").trim(),
  description: String(goal.description || "").trim(),
  successCriteria: String(goal.successCriteria || "").trim(),
  category: String(goal.category || "").trim(),
  source: String(goal.source || "").trim(),
  supportStrategy: String(goal.supportStrategy || "").trim(),
  status: String(goal.status || "active").trim() || "active",
  startDate: String(goal.startDate || "").trim(),
  targetDate: String(goal.targetDate || "").trim(),
});

export const normalizeCareGoals = (value = []) => (
  Array.isArray(value)
    ? value
      .map(normalizeCareGoal)
      .filter((goal) => goal.title)
    : []
);

export const createEmptySupportProfile = () => ({
  about: "",
  emergencyContacts: "",
  whoTheyAre: {
    favoriteActivities: [],
    interests: [],
    comfortItems: [],
    favoriteFoods: [],
    notes: "",
  },
  whatHelps: [],
  whatCanBeDifficult: [],
  earlyWarningSigns: [],
  thingsThatUsuallyHelp: [],
  thingsToAvoid: [],
  safetyConsiderations: [],
  communicationStyle: [],
  communicationNotes: "",
  careGoals: [],
  familySupportTips: [],
  schoolTherapySuggestions: "",
});

export const normalizeSupportProfile = (supportProfile = {}) => {
  const whoTheyAre = supportProfile?.whoTheyAre || {};

  return {
    about: String(supportProfile?.about || "").trim(),
    emergencyContacts: String(supportProfile?.emergencyContacts || "").trim(),
    whoTheyAre: {
      favoriteActivities: normalizeStringArray(whoTheyAre.favoriteActivities),
      interests: normalizeStringArray(whoTheyAre.interests),
      comfortItems: normalizeStringArray(whoTheyAre.comfortItems),
      favoriteFoods: normalizeStringArray(whoTheyAre.favoriteFoods),
      notes: String(whoTheyAre.notes || "").trim(),
    },
    whatHelps: normalizeStringArray(supportProfile?.whatHelps),
    whatCanBeDifficult: normalizeStringArray(supportProfile?.whatCanBeDifficult),
    earlyWarningSigns: normalizeStringArray(supportProfile?.earlyWarningSigns),
    thingsThatUsuallyHelp: normalizeStringArray(supportProfile?.thingsThatUsuallyHelp),
    thingsToAvoid: normalizeStringArray(supportProfile?.thingsToAvoid),
    safetyConsiderations: normalizeStringArray(supportProfile?.safetyConsiderations),
    communicationStyle: normalizeStringArray(supportProfile?.communicationStyle),
    communicationNotes: String(supportProfile?.communicationNotes || "").trim(),
    careGoals: normalizeCareGoals(supportProfile?.careGoals),
    familySupportTips: normalizeStringArray(supportProfile?.familySupportTips),
    schoolTherapySuggestions: String(supportProfile?.schoolTherapySuggestions || "").trim(),
  };
};

export const hasSupportProfileValue = (supportProfile = {}) => {
  const normalized = normalizeSupportProfile(supportProfile);
  return Boolean(
    normalized.about
    || normalized.emergencyContacts
    || normalized.whoTheyAre.favoriteActivities.length
    || normalized.whoTheyAre.interests.length
    || normalized.whoTheyAre.comfortItems.length
    || normalized.whoTheyAre.favoriteFoods.length
    || normalized.whoTheyAre.notes
    || normalized.whatHelps.length
    || normalized.whatCanBeDifficult.length
    || normalized.earlyWarningSigns.length
    || normalized.thingsThatUsuallyHelp.length
    || normalized.thingsToAvoid.length
    || normalized.safetyConsiderations.length
    || normalized.communicationStyle.length
    || normalized.communicationNotes
    || normalized.careGoals.length
    || normalized.familySupportTips.length
    || normalized.schoolTherapySuggestions
  );
};
